import React, { useEffect, useMemo, useState } from "react";

type GuideHandOverlayProps = {
    open?: boolean;
    onClose?: () => void;          // '확인' 버튼/딤 클릭 시 호출
    onceKey?: string;              // 예: "guide.hand.shown"
    message?: React.ReactNode;
    frameUrls?: string[];
    frameDurationMs?: number;
    holeHeightPx?: number;         // 하단 클릭-스루 구멍 높이 (기본 63px)
    zIndex?: number;
    showHoleEdge?: boolean;        // 구멍 경계선 표시
};

const defaultFrames = [
    "/Images/hand_01.png",
    "/Images/hand_02.png",
    "/Images/hand_03.png",
    "/Images/hand_02.png",
];

export default function GuideHandOverlay({
    open,
    onClose,
    onceKey,
    message,
    frameUrls = defaultFrames,
    frameDurationMs = 80,
    holeHeightPx = 57,
    zIndex = 9999,
    showHoleEdge = true,
}: GuideHandOverlayProps) {
    //localStorage.removeItem(onceKey || ""); // 한 번만 보기 이력 삭제하려면 주석 해제!
    const alreadySeen = useMemo(() => (onceKey ? localStorage.getItem(onceKey) === "1" : false), [onceKey]);
    const [visible, setVisible] = useState<boolean>(() => (typeof open === "boolean" ? open : !alreadySeen));

    useEffect(() => {
        if (typeof open === "boolean") setVisible(open);
    }, [open]);

    // 프레임 애니메이션
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (!visible) return;
        const id = window.setInterval(() => setIdx((i) => (i + 1) % frameUrls.length), frameDurationMs);
        return () => window.clearInterval(id);
    }, [visible, frameUrls.length, frameDurationMs]);

    const close = () => {
        if (onceKey) localStorage.setItem(onceKey, "1");
        setVisible(false);
        onClose?.();
    };

    if (!visible) return null;

    const safeBottom = "env(safe-area-inset-bottom, 0px)";
    const holeBottom = `calc(${holeHeightPx}px + ${safeBottom})`;
    const fingerOffset = "10px"; // 손가락과 구멍 사이 간격

    // 전체 컨테이너: 클릭 패스스루
    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        inset: 0,
        zIndex,
        pointerEvents: "none",
    };

    // 위쪽 딤: 하단 구멍 남김 (이 영역만 클릭 막힘 → 닫기 가능)
    const dimTopStyle: React.CSSProperties = {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: holeBottom,
        background: "rgba(0,0,0,0.8)",
        pointerEvents: "auto",
    };

    // 손가락/문구/확인버튼 묶음: 하단 구멍 위 10px 고정
    const bottomAnchorStyle: React.CSSProperties = {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: `calc(${holeBottom} + ${fingerOffset})`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        textAlign: "left",
        color: "#fff",
        userSelect: "none",
        padding: "0 12px",
        zIndex: zIndex + 1,
        pointerEvents: "auto", // 클릭 허용
    };

    const textStyle: React.CSSProperties = {
        maxWidth: 420,
        lineHeight: 1.5,
        fontSize: 16,
        textShadow: "0 1px 2px rgba(0,0,0,0.45)",
    };

    const confirmBtnStyle: React.CSSProperties = {
        minWidth: 120,
        height: 40,
        padding: "0 16px",
        borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.25)",
        background: "rgba(255,255,255,0.95)",
        color: "#222",
        fontWeight: 600,
        cursor: "pointer",
    };

    const handImgStyle: React.CSSProperties = {
        width: 120,
        height: 120,
        objectFit: "contain",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))",
    };

    const holeEdgeStyle: React.CSSProperties = {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: holeBottom,
        height: 1,
        background: "linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(255,255,255,0))",
        pointerEvents: "none",
    };

    return (
        <div style={overlayStyle}>
            {/* 위쪽 딤(탭 시 닫기). 하단 63px은 구멍 */}
            <div style={dimTopStyle} />

            {/* 순서: 문구 → 확인 버튼 → 손가락 이미지 */}
            <div style={bottomAnchorStyle} onClick={(e) => e.stopPropagation()}>
                <div style={textStyle}>
                    {message ?? <>
                        - [다시] 버튼 :<br />&nbsp;&nbsp;현재 컨텐츠가 새로고침 됩니다.<br /><br />
                        - [랜덤] 버튼 :<br />&nbsp;&nbsp;등록된 순서에 관계없이 무작위의 컨텐츠가 표시<br />&nbsp;&nbsp;됩니다.<br /><br />
                        - [순차] 버튼 :<br />&nbsp;&nbsp;등록된 순서대로 볼 수 있는 '이전/다음' 버튼이<br />&nbsp;&nbsp;표시됩니다.<br /><br />
                        (확인 버튼을 클릭하면 이 메세지는 사라집니다.)<br /><br />
                    </>}
                </div>
                <button type="button" style={confirmBtnStyle} onClick={close}>
                    확인
                </button>
                <img src={frameUrls[idx]} alt="guide hand" style={handImgStyle} draggable={false} />
            </div>

            {showHoleEdge && <div style={holeEdgeStyle} />}
        </div>
    );
}
